// Account menu, login and registration. Trading Preview always uses the
// account's primary managed wallet; signing and broadcast are separate gates.
//
// Registration is its own server gate (WT_USERLAND_EMAIL_REGISTRATION_ENABLED)
// and is reported by /v1/userland/trade-capabilities. Production runs it OFF,
// so this surface shows Sign in alone there and grows the Create-account form
// only where the route is actually mounted.

import { CopyIcon, CrownSimpleIcon, EnvelopeSimpleIcon, SignOutIcon } from '@phosphor-icons/react'
import {
  type FormEvent,
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import {
  type AuthSession,
  MIN_ACCOUNT_PASSWORD_LENGTH,
  type MeSnapshot,
  loginEmail,
  logoutSession,
  registerEmail,
} from '../../api/userland'
import { AccountVaultMigrationCommittedError } from '../../hooks/useAccountVault'
import { shortAddr } from '../../lib/format'
import {
  accountAvatarSrc,
  profileAccountName,
  profileLoginIdentity,
} from '../../lib/profileIdentity'
import { generateRecoveryKey } from '../../lib/recoveryKey'
import { probeTradeCapabilities } from '../../lib/tradeCapabilities'
import { useAccountSession } from '../../wallet/AccountSessionContext'
import { useAuthShell } from '../../wallet/AuthShellContext'
import { useTradingSession } from '../../wallet/TradingSessionContext'
import { useWallets } from '../../wallet/WalletsContext'
import { SocialGlyph } from '../common/SocialLinks'
import { TonLogo } from '../exchange/icons'
import { Described } from '../ui/Described'
import { useCopyToast } from '../ui/copyToast'
import { figs } from '../ui/fig'
import { useOptionalWhatsNew } from '../whatsnew/WhatsNewContext'
import { AccountCard } from './AccountCard'
import { type AccountFigures, rankName, rankOf, tierOf, useAccountFigures } from './accountFigures'
import styles from './profile.module.css'

type AccessMode = 'signin' | 'register'

type AccessPanelProps = {
  session: AuthSession | null
  loginEnabled: boolean
  registrationEnabled: boolean
  signingEnabled: boolean
  executionEnabled: boolean
  tradingStatusNote: string | null
  confirmRequired: boolean
  migrationRequired: boolean
  legacy: boolean
  walletAddress?: string
  mode: AccessMode
  email: string
  password: string
  confirmPassword: string
  migrationPassword: string
  busy: boolean
  error: string | null
  onModeChange: (mode: AccessMode) => void
  onEmailChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onConfirmPasswordChange: (value: string) => void
  onMigrationPasswordChange: (value: string) => void
  onLogin: (event: FormEvent) => void
  onConfirm: (event: FormEvent) => void
  onRegister: (event: FormEvent) => void
  onMigrate: (event: FormEvent) => void
  onLegacyUnlock: (event: FormEvent) => void
  onCancelMigration: () => void
  onOpenWallets: () => void
}

export function ProfilePopover({
  onClose,
  onOpenSettings,
}: {
  onClose: () => void
  onOpenSettings: () => void
}) {
  const navigate = useNavigate()
  const { openAuth } = useAuthShell()
  const { active, reload } = useWallets()
  const { vault } = useTradingSession()
  const { status: accountStatus, me, reload: reloadSession } = useAccountSession()
  const figures = useAccountFigures(me)
  // the door to What's New, where the app's layout gives one
  const openWhatsNew = useOptionalWhatsNew()
  const [session, setSession] = useState<AuthSession | null>(null)
  const sessionResolving = accountStatus === 'loading' && !session && !me
  const [mode, setMode] = useState<AccessMode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // Shown once, immediately after registration mints the keyring.
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null)
  const [migrationPassword, setMigrationPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loginEnabled, setLoginEnabled] = useState(false)
  const [registrationEnabled, setRegistrationEnabled] = useState(false)
  const [signingEnabled, setSigningEnabled] = useState(false)
  const [executionEnabled, setExecutionEnabled] = useState(false)
  const [signInRequired, setSignInRequired] = useState(false)

  useEffect(() => {
    if (me) setSession({ authenticated: true, account_id: me.account_id })
  }, [me])

  useEffect(() => {
    let alive = true
    void probeTradeCapabilities().then((capabilities) => {
      if (!alive) return
      setLoginEnabled(capabilities.loginEnabled)
      setRegistrationEnabled(capabilities.registrationEnabled)
      setSigningEnabled(capabilities.signingEnabled)
      setExecutionEnabled(capabilities.executionEnabled)
    })
    return () => {
      alive = false
    }
  }, [])

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    setSignInRequired(false)
    const submittedPassword = password
    // Remove the user-held secret from React state / the DOM before the first
    // network await. Only this local stack value survives long enough for the
    // one explicit auth + in-memory unlock attempt.
    setPassword('')
    try {
      let value: AuthSession
      try {
        value = await loginEmail(email, submittedPassword)
      } catch {
        setError('Email or password is incorrect')
        return
      }
      setSession(value)
      void reloadSession()
      try {
        await vault.confirmAccountPassword(submittedPassword)
      } catch (unlockError) {
        // A v2 keyring created with the former, separate vault password enters
        // the one-time re-wrap form. Other failures stay fail-closed and ask
        // for another explicit account confirmation.
        if (!isPasswordMigrationRequired(unlockError)) setSignInRequired(true)
      }
      await reload()
    } finally {
      setPassword('')
      setBusy(false)
    }
  }

  const submitRegistration = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    const submittedPassword = password
    if (submittedPassword.length < MIN_ACCOUNT_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters`)
      return
    }
    if (submittedPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setBusy(true)
    setSignInRequired(false)
    // Same rule as login: the user-held secret leaves React state and the DOM
    // before the first network await.
    setPassword('')
    setConfirmPassword('')
    try {
      let value: AuthSession
      try {
        value = await registerEmail(email, submittedPassword)
      } catch (registerError) {
        setError(registrationErrorMessage(registerError))
        return
      }
      setSession(value)
      setMode('signin')
      try {
        // A brand-new account has no keyring yet, so this does not unlock
        // anything — it primes account security with the password just chosen.
        await vault.confirmAccountPassword(submittedPassword)
        // Mint the keyring HERE, while the password is primed. The recovery
        // slot is mandatory, and it belongs to the account — so it is issued
        // once, now, instead of ambushing the user at their first wallet.
        // After this, creating or importing a wallet asks for nothing.
        const key = generateRecoveryKey()
        await vault.setup(key)
        setRecoveryKey(key)
      } catch {
        setSignInRequired(true)
      }
      await reload()
    } finally {
      setPassword('')
      setConfirmPassword('')
      setBusy(false)
    }
  }

  // Re-confirming an ALREADY AUTHENTICATED session. The v2 keyring is unwrapped
  // with the password locally, so this needs no email and no second login — the
  // unwrap itself is what proves the password. Asking for the email here was
  // the reason revealing a seed demanded credentials twice.
  const submitConfirmation = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const submittedPassword = password
    setPassword('')
    try {
      await vault.confirmAccountPassword(submittedPassword)
      setSignInRequired(false)
      await reload()
    } catch (confirmError) {
      // A v2 slot created with the former separate vault password hands off to
      // the one-time migration form instead of reporting a wrong password.
      if (isPasswordMigrationRequired(confirmError)) return
      setError('Password is incorrect')
    } finally {
      setPassword('')
      setBusy(false)
    }
  }

  const submitMigration = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const submittedPassword = migrationPassword
    setMigrationPassword('')
    try {
      await vault.migratePassword(submittedPassword)
      setSignInRequired(false)
    } catch (migrationError) {
      if (migrationError instanceof AccountVaultMigrationCommittedError) {
        setError('Account security was updated. Sign in again to resume trading.')
      } else if (
        migrationError instanceof Error &&
        migrationError.message.toLowerCase().includes('vault changed')
      ) {
        setError('Account security changed in another session. Sign in again before retrying.')
      } else {
        setError('Previous wallet password is incorrect')
      }
    } finally {
      setMigrationPassword('')
      setBusy(false)
    }
  }

  const submitLegacyUnlock = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const submittedPassword = migrationPassword
    setMigrationPassword('')
    try {
      await vault.unlock({ password: submittedPassword })
      setSignInRequired(false)
    } catch {
      setError('Previous account password is incorrect')
    } finally {
      setMigrationPassword('')
      setBusy(false)
    }
  }

  const go = (path: string) => {
    navigate(path)
    onClose()
  }

  // The page dims behind the open menu, as behind every Soft popover. The dim
  // is portaled out of the header's wrapper, so the header's own outside-press
  // hook closes the menu on it; the press hands the focus back to the avatar
  // the menu hangs from (its previous sibling) instead of dropping it on <body>.
  const popRef = useRef<HTMLElement>(null)
  const returnFocus = (event: MouseEvent) => {
    event.preventDefault()
    const trigger = popRef.current?.previousElementSibling
    if (trigger instanceof HTMLElement) trigger.focus()
  }

  // The menu has one composition, the card (the operator's pick, 2026-10-13);
  // its name stays on the root, where the stylesheet and the tests read it,
  // and the rank's metal with it. Set from here rather than written on the
  // element — the root's own line belongs to more than one change at a time.
  const metal = tierOf(rankOf(figures))?.metal
  useLayoutEffect(() => {
    const root = popRef.current
    if (!root) return
    root.setAttribute('data-variant', 'current')
    if (metal) root.style.setProperty('--rank-metal', metal)
    else root.style.removeProperty('--rank-metal')
  }, [metal])

  return (
    <>
      {createPortal(
        <div className={styles.scrim} data-scrim="" aria-hidden="true" onMouseDown={returnFocus} />,
        document.body
      )}
      <section className={styles.popover} ref={popRef} aria-label="Account menu">
        <div className={styles.header}>
          <AccountAvatar me={me} className={styles.avatar ?? ''} />
          <div className={styles.identity}>
            <span className={styles.name}>
              {me ? profileAccountName(me) : session ? 'Account' : ' '}
            </span>
            {me ? (
              <IdentityLine me={me} />
            ) : (
              <span className={styles.uidText}>
                {sessionResolving ? ' ' : guestSubtitle(loginEnabled, registrationEnabled, mode)}
              </span>
            )}
          </div>
          <RankTag figures={figures} />
        </div>

        {recoveryKey ? (
          <AccountRecoveryKeyPanel
            recoveryKey={recoveryKey}
            onAcknowledge={() => setRecoveryKey(null)}
          />
        ) : sessionResolving ? null : (
          <AccessPanel
            session={session}
            loginEnabled={loginEnabled}
            registrationEnabled={registrationEnabled}
            signingEnabled={signingEnabled}
            executionEnabled={executionEnabled}
            tradingStatusNote={tradingStatusNote(
              signingEnabled,
              signInRequired,
              vault.status,
              vault.migrationRequired
            )}
            confirmRequired={false}
            migrationRequired={vault.migrationRequired}
            legacy={vault.legacy}
            walletAddress={active?.address}
            mode={mode}
            email={email}
            password={password}
            confirmPassword={confirmPassword}
            migrationPassword={migrationPassword}
            busy={busy}
            error={error}
            onModeChange={(next) => {
              setMode(next)
              setError(null)
              setPassword('')
              setConfirmPassword('')
            }}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onMigrationPasswordChange={setMigrationPassword}
            onLogin={submitLogin}
            onConfirm={submitConfirmation}
            onRegister={submitRegistration}
            onMigrate={submitMigration}
            onLegacyUnlock={submitLegacyUnlock}
            onCancelMigration={() => {
              // Drop the primed migration state so the panel returns to the plain
              // password form instead of insisting an update is owed.
              void vault.forgetDevice()
              setMigrationPassword('')
              setError(null)
            }}
            onOpenWallets={() => go('/portfolio')}
          />
        )}

        <AccountCard
          figures={figures}
          // a signed-in account whose keyring this browser cannot open
          restorable={Boolean(session) && vault.status === 'locked' && !vault.legacy}
          onSettings={() => {
            onOpenSettings()
            onClose()
          }}
          onWhatsNew={
            openWhatsNew
              ? () => {
                  onClose()
                  openWhatsNew()
                }
              : undefined
          }
          onRestore={() => {
            onClose()
            openAuth('ton-unlock')
          }}
        />

        <div className={styles.foot}>
          <button
            type="button"
            className={styles.disconnect}
            disabled={!session}
            onClick={async () => {
              try {
                // RAM only. Wiping the origin-bound device key here is what made
                // TON Connect reconnect print "Could not sign this trade": login
                // proves the account, it cannot unwrap a deleted private half.
                await vault.lock()
              } finally {
                await logoutSession()
                setSession(null)
                setSignInRequired(false)
                void reload()
              }
            }}
          >
            <SignOutIcon size={16} weight="bold" aria-hidden="true" />
            {figs('Log out')}
          </button>
        </div>
      </section>
    </>
  )
}

function AccessPanel(props: AccessPanelProps) {
  // `confirmRequired` is derived from `session`, so it can never be true for a
  // guest — which keeps the two halves of this panel genuinely disjoint.
  if (!props.session) return <GuestAccessPanel {...props} />
  if (props.migrationRequired) return <VaultMigrationForm {...props} />
  if (props.confirmRequired && props.legacy) return <LegacyVaultUnlockForm {...props} />
  return null
}

// Signed out: sign in, or register where the server says the route exists.
function GuestAccessPanel(props: AccessPanelProps) {
  if (!props.loginEnabled && !props.registrationEnabled) return null
  const showRegister =
    props.registrationEnabled && (props.mode === 'register' || !props.loginEnabled)
  return showRegister ? <AccountRegisterForm {...props} /> : <AccountLoginForm {...props} />
}

// The quiet line under the primary action that swaps sign-in for registration.
// Rendered inside the form so the panel keeps its single bottom divider.
function AccessModeSwitch({
  mode,
  onModeChange,
}: {
  mode: AccessMode
  onModeChange: (mode: AccessMode) => void
}) {
  const toRegister = mode === 'signin'
  return (
    <span className={styles.modeSwitch}>
      {toRegister ? 'New to wils?' : 'Already have an account?'}
      <button
        type="button"
        className={styles.modeSwitchAction}
        onClick={() => onModeChange(toRegister ? 'register' : 'signin')}
      >
        {toRegister ? 'Create account' : 'Sign in'}
      </button>
    </span>
  )
}

// Issued once, right after registration. This is the ACCOUNT's key — the way
// back in if the password is lost — not a per-wallet ceremony. Creating and
// importing wallets ask for nothing after this.
function AccountRecoveryKeyPanel({
  recoveryKey,
  onAcknowledge,
}: {
  recoveryKey: string
  onAcknowledge: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [saved, setSaved] = useState(false)
  return (
    <div className={styles.loginForm}>
      <span className={styles.protectionNote}>
        Save your account recovery key. It is the only way back into this account if the password is
        lost — it is shown once and never again.
      </span>
      <code className={styles.recoveryKey}>{recoveryKey}</code>
      <button
        type="button"
        className={styles.secondaryAction}
        onClick={() => {
          navigator.clipboard
            ?.writeText(recoveryKey)
            .then(() => setCopied(true))
            .catch(() => {})
        }}
      >
        {copied ? 'Copied' : 'Copy key'}
      </button>
      <label className={styles.recoveryConfirm}>
        <input
          type="checkbox"
          checked={saved}
          onChange={(event) => setSaved(event.target.checked)}
        />
        I saved the recovery key
      </label>
      <button
        type="button"
        className={styles.primaryAction}
        disabled={!saved}
        onClick={onAcknowledge}
      >
        Continue
      </button>
    </div>
  )
}

function AccountRegisterForm(props: AccessPanelProps) {
  return (
    <form className={styles.loginForm} onSubmit={props.onRegister}>
      <label className={styles.fieldLabel}>
        Email
        <input
          className={styles.field}
          type="email"
          autoComplete="email"
          value={props.email}
          onChange={(event) => props.onEmailChange(event.target.value)}
          required
        />
      </label>
      <label className={styles.fieldLabel}>
        Password
        <input
          className={styles.field}
          type="password"
          autoComplete="new-password"
          minLength={MIN_ACCOUNT_PASSWORD_LENGTH}
          value={props.password}
          onChange={(event) => props.onPasswordChange(event.target.value)}
          required
        />
      </label>
      <label className={styles.fieldLabel}>
        Confirm password
        <input
          className={styles.field}
          type="password"
          autoComplete="new-password"
          value={props.confirmPassword}
          onChange={(event) => props.onConfirmPasswordChange(event.target.value)}
          required
        />
      </label>
      <span className={styles.protectionNote}>
        At least {MIN_ACCOUNT_PASSWORD_LENGTH} characters. This password also unlocks your wallets
        and cannot be reset — you save a recovery key when you create your first wallet.
      </span>
      {props.error && <p className={styles.error}>{props.error}</p>}
      <button type="submit" className={styles.primaryAction} disabled={props.busy}>
        {props.busy ? 'Creating…' : 'Create account'}
      </button>
      {props.loginEnabled ? (
        <AccessModeSwitch mode="register" onModeChange={props.onModeChange} />
      ) : null}
    </form>
  )
}

function LegacyVaultUnlockForm(props: AccessPanelProps) {
  return (
    <form className={styles.loginForm} onSubmit={props.onLegacyUnlock}>
      <span className={styles.protectionNote}>
        This account was created before the current security model. Enter its previous password.
      </span>
      <label className={styles.fieldLabel}>
        Previous account password
        <input
          className={styles.field}
          type="password"
          autoComplete="current-password"
          value={props.migrationPassword}
          onChange={(event) => props.onMigrationPasswordChange(event.target.value)}
          required
        />
      </label>
      {props.error && <p className={styles.error}>{props.error}</p>}
      <button type="submit" className={styles.primaryAction} disabled={props.busy}>
        {props.busy ? 'Unlocking…' : 'Unlock legacy wallet'}
      </button>
    </form>
  )
}

// Reached whenever the password fails to unwrap the keyring. That is NOT proof
// an update is owed: a mistyped password fails identically, and an account whose
// keyring was created with its account password has no "previous" password to
// enter, so telling it an update is required sends it into a form that cannot
// succeed. Say what is actually known, and offer the way back.
function VaultMigrationForm(props: AccessPanelProps) {
  return (
    <form className={styles.loginForm} onSubmit={props.onMigrate}>
      <span className={styles.protectionNote}>
        That password did not unlock this account. If you have simply mistyped it, go back and try
        again. Only accounts created before the current security model have a separate, earlier
        wallet password — enter it here to move to your account password.
      </span>
      <label className={styles.fieldLabel}>
        Previous wallet password
        <input
          className={styles.field}
          type="password"
          autoComplete="current-password"
          value={props.migrationPassword}
          onChange={(event) => props.onMigrationPasswordChange(event.target.value)}
          required
        />
      </label>
      {props.error && <p className={styles.error}>{props.error}</p>}
      <button type="submit" className={styles.primaryAction} disabled={props.busy}>
        {props.busy ? 'Updating…' : 'Update account security'}
      </button>
      <span className={styles.modeSwitch}>
        Mistyped it?
        <button type="button" className={styles.modeSwitchAction} onClick={props.onCancelMigration}>
          Try the password again
        </button>
      </span>
    </form>
  )
}

function AccountLoginForm(props: AccessPanelProps) {
  return (
    <form className={styles.loginForm} onSubmit={props.onLogin}>
      <label className={styles.fieldLabel}>
        Email
        <input
          className={styles.field}
          type="email"
          autoComplete="email"
          value={props.email}
          onChange={(event) => props.onEmailChange(event.target.value)}
          required
        />
      </label>
      <label className={styles.fieldLabel}>
        Password
        <input
          className={styles.field}
          type="password"
          autoComplete="current-password"
          value={props.password}
          onChange={(event) => props.onPasswordChange(event.target.value)}
          required
        />
      </label>
      {props.error && <p className={styles.error}>{props.error}</p>}
      <button type="submit" className={styles.primaryAction} disabled={props.busy}>
        {props.busy ? 'Signing in…' : 'Sign in'}
      </button>
      {props.registrationEnabled && !props.confirmRequired ? (
        <AccessModeSwitch mode="signin" onModeChange={props.onModeChange} />
      ) : null}
    </form>
  )
}

// The login the account is known by, built as the wallets popover builds a
// wallet's address line: the provider's mark, the text, and — where there is
// something to copy (a TON address) — the copy mark 5 after it: the product's
// one copy answer (ui/copyToast), the toast once the address is on the
// clipboard, the mark itself never changing.
function IdentityLine({ me }: { me: MeSnapshot }) {
  const identity = profileLoginIdentity(me)
  const copyValue = identity.kind === 'ton' ? identity.copyValue : ''
  const addr = identity.kind === 'ton' && copyValue ? shortAddr(copyValue) : null
  const shown = addr ?? identity.label
  const copyToast = useCopyToast()
  const copy = copyValue ? (
    <button
      type="button"
      className={styles.copy}
      aria-label="Copy TON address"
      onClick={() => copyToast(copyValue, 'Wallet address copied')}
    >
      <CopyIcon size={13} weight="bold" aria-hidden="true" />
    </button>
  ) : null
  return (
    <span className={styles.login} data-kind={identity.kind}>
      <span className={styles.loginMark} aria-hidden="true">
        <IdentityMark kind={identity.kind} />
      </span>
      {/* a TON address is had from its copy mark (round 9); a clipped email
          stays whole in its text */}
      <span className={styles.loginText}>{shown}</span>
      {copy}
    </span>
  )
}

// The rewards rank of the primary wallet, as the Rewards page reads it: a
// quiet tag at the head's right, its mark in the tier's metal. While the read
// is on its way its place is held, so the name beside it does not re-flow; a
// read that failed, or an account with no wallet, draws nothing.
function RankTag({ figures }: { figures: AccountFigures }) {
  const rank = rankOf(figures)
  if (!rank) {
    return figures.rewardsLoading ? <span className={styles.rankHold} aria-hidden="true" /> : null
  }
  const tier = tierOf(rank)
  return (
    <Described as="span" className={styles.rank} description="Rewards rank of the primary wallet">
      <CrownSimpleIcon
        size={13}
        weight="fill"
        aria-hidden="true"
        style={tier ? { color: tier.metal } : undefined}
      />
      {figs(rankName(rank))}
    </Described>
  )
}

function AccountAvatar({ me, className }: { me: MeSnapshot | null; className: string }) {
  const src = accountAvatarSrc(me)
  if (!src) return <span className={className} />
  return (
    <span className={className}>
      <img src={src} alt="" />
    </span>
  )
}

function IdentityMark({ kind }: { kind: ReturnType<typeof profileLoginIdentity>['kind'] }) {
  if (kind === 'ton') return <TonLogo size={13} />
  if (kind === 'telegram') return <SocialGlyph type="telegram" size={13} />
  if (kind === 'google') return <GoogleMark />
  return <EnvelopeSimpleIcon size={13} weight="bold" aria-hidden="true" />
}

function GoogleMark() {
  return (
    <svg width="13" height="13" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.6 32.4 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5Z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7 12.9 19.6C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7Z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.1 0 9.8-1.9 13.3-5.2l-6.1-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.5-11.2-8.3l-6.5 5C9.5 39.6 16.2 44 24 44Z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.8-6.6 7.3l6.1 5.2C38.2 37.3 44 31.5 44 24c0-1.2-.1-2.3-.4-3.5Z"
      />
    </svg>
  )
}

function guestSubtitle(
  loginEnabled: boolean,
  registrationEnabled: boolean,
  mode: AccessMode
): string {
  if (registrationEnabled && (mode === 'register' || !loginEnabled)) {
    return 'Create an account to trade'
  }
  if (loginEnabled) return 'Sign in to trade'
  return 'Preview access is not enabled'
}

// email_taken is deliberately explicit: unlike login, registration cannot hide
// that an address exists — the server already answers 409 for it — and a user
// who cannot tell "taken" from "wrong" has no next step.
function registrationErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Could not create the account. Try again.'
  switch (error.code) {
    case 'email_taken':
      return 'This email is already registered — sign in instead'
    case 'password_too_weak':
      return `Password must be at least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters`
    case 'bad_request':
      return 'Enter a valid email address'
    default:
      return error.status === 404
        ? 'Registration is not enabled on this deployment'
        : 'Could not create the account. Try again.'
  }
}

function isPasswordMigrationRequired(error: unknown): boolean {
  return error instanceof Error && error.message === 'account vault password migration required'
}

function tradingStatusNote(
  signingEnabled: boolean,
  signInRequired: boolean,
  vaultStatus: string,
  migrationRequired: boolean
): string | null {
  if (!signingEnabled) return null
  // Same correction as the form below: an unwrap failure is not proof an update
  // is owed, because a mistyped password fails identically.
  if (migrationRequired) return 'That password did not unlock this account'
  if (signInRequired || vaultStatus === 'locked') {
    return null
  }
  if (vaultStatus === 'unlocked') return null
  if (vaultStatus === 'none') return 'Trading setup is incomplete'
  if (vaultStatus === 'offline') return 'Trading is temporarily unavailable'
  return 'Checking trading access…'
}
